import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, PenTool, Hammer, KeyRound } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: MessageSquare,
    title: 'Consultation & Requirement Analysis',
    description: 'We begin by understanding your vision, lifestyle, and specific requirements through detailed discussions.',
  },
  {
    number: '02',
    icon: PenTool,
    title: 'Design & Planning (2D + 3D)',
    description: 'Our designers create comprehensive floor plans and stunning 3D visualizations for your approval.',
  },
  {
    number: '03',
    icon: Hammer,
    title: 'Execution & Supervision',
    description: 'Our project managers oversee every detail of the implementation to ensure quality and precision.',
  },
  {
    number: '04',
    icon: KeyRound,
    title: 'Final Handover',
    description: 'We complete the project with a thorough walkthrough and ensure your complete satisfaction.',
  },
];

export default function Process() {
  return (
    <section className="relative py-20 md:py-28 bg-[#FDFBF7] overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#C9A962]/10 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#C9A962]/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Decorative Grid */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'linear-gradient(#C9A962 1px, transparent 1px), linear-gradient(90deg, #C9A962 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      <div className="max-w-7xl mx-auto px-6 md:px-16 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-14 md:mb-16"
        >
          <motion.div 
            className="inline-flex items-center gap-3 mb-4 px-5 py-2 border border-[#C9A962]/20 bg-white/50 backdrop-blur-sm"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="w-1.5 h-1.5 bg-[#C9A962] rounded-full animate-pulse" />
            <span className="text-[#C9A962] tracking-[0.25em] text-[10px] uppercase font-medium">How We Work</span>
            <div className="w-1.5 h-1.5 bg-[#C9A962] rounded-full animate-pulse" />
          </motion.div>
          
          <motion.h2 
            className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-4 leading-[1.1]"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Our Design
            <span className="block mt-1 bg-gradient-to-r from-[#C9A962] via-[#d4b76a] to-[#C9A962] bg-clip-text text-transparent">
              Process
            </span>
          </motion.h2>
          
          <motion.p 
            className="text-[#2C2C2C]/60 max-w-2xl mx-auto text-base leading-relaxed"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            A seamless journey from initial concept to the final reveal of your dream space.
          </motion.p>
        </motion.div>

        {/* Process Steps */}
        <div className="relative">
          {/* Animated Connection Line */}
          <div className="hidden lg:block absolute top-24 left-[10%] right-[10%] h-[1px] overflow-hidden">
            <motion.div 
              className="h-full bg-gradient-to-r from-transparent via-[#C9A962]/30 to-transparent"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.4 }}
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-5">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 0.6, 
                  delay: index * 0.12,
                  ease: [0.22, 1, 0.36, 1]
                }}
                className="relative group"
              >
                {/* Card Container */}
                <div className="relative h-full bg-white/60 backdrop-blur-sm border border-[#C9A962]/20 p-6 hover:border-[#C9A962]/40 hover:shadow-[0_8px_30px_rgba(201,169,98,0.12)] transition-all duration-500 overflow-hidden">
                  {/* Hover Glow Effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#C9A962]/5 to-transparent" />
                  </div>

                  {/* Step Number Badge */}
                  <motion.div 
                    className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-[#C9A962] to-[#b8964d] flex items-center justify-center shadow-[0_4px_20px_rgba(201,169,98,0.25)] group-hover:shadow-[0_6px_30px_rgba(201,169,98,0.35)] transition-shadow duration-500"
                    whileHover={{ scale: 1.08, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <span className="font-serif text-lg text-black font-bold">{step.number}</span>
                  </motion.div>

                  {/* Icon Container */}
                  <div className="relative mb-6">
                    <motion.div 
                      className="w-16 h-16 border border-[#C9A962]/30 flex items-center justify-center group-hover:border-[#C9A962] transition-all duration-500 relative bg-white/80"
                      whileHover={{ scale: 1.05 }}
                    >
                      {/* Icon Glow */}
                      <div className="absolute inset-0 bg-[#C9A962]/0 group-hover:bg-[#C9A962]/10 blur-xl transition-all duration-500" />
                      
                      <step.icon className="w-7 h-7 text-[#C9A962] relative z-10 group-hover:scale-110 transition-transform duration-500" strokeWidth={1.5} />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <h3 className="font-serif text-xl text-[#2C2C2C] mb-3 leading-tight group-hover:text-[#C9A962] transition-colors duration-500">
                      {step.title}
                    </h3>
                    <p className="text-[#2C2C2C]/60 text-sm leading-relaxed group-hover:text-[#2C2C2C]/80 transition-colors duration-500">
                      {step.description}
                    </p>
                  </div>

                  {/* Decorative Corner */}
                  <div className="absolute bottom-0 right-0 w-10 h-10 border-r border-b border-[#C9A962]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>

                {/* Arrow for desktop */}
                {index < steps.length - 1 && (
                  <motion.div 
                    className="hidden lg:block absolute top-24 -right-2.5 transform -translate-y-1/2 z-20"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.12 + 0.6 }}
                  >
                    <div className="w-7 h-7 bg-[#C9A962] flex items-center justify-center shadow-[0_0_15px_rgba(201,169,98,0.3)]">
                      <svg className="w-3.5 h-3.5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-14"
        >
          <motion.a
            href="#contact"
            className="group relative inline-flex items-center bg-gradient-to-r from-[#C9A962] to-[#b8964d] text-black px-8 py-3.5 text-base font-medium overflow-hidden"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Shine Effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.6 }}
            />
            
            <span className="relative z-10">Start Your Journey Today</span>
            
            <svg className="relative z-10 ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}